import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import Auth from "./Auth";
import { useUser } from "../../context/UserContext";
import { ErrorHandler } from "../../utils/errorHandler";
import { toast } from "react-toastify";
import { resolveAuthRedirect } from "../../utils/authRedirect";
import "react-toastify/dist/ReactToastify.css";

const fieldClass = (hasError) =>
  `w-full px-3.5 py-3 rounded-xl border bg-gray-50 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:bg-white transition-colors ${
    hasError
      ? "border-red-400 focus:ring-red-200"
      : "border-gray-200 focus:ring-orange-200 focus:border-orange-400"
  }`;

const Login = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useUser();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    const nextErrors = {};
    if (!formData.email.trim()) {
      nextErrors.email = "Veuillez renseigner votre email";
    } else if (!/\S+@\S+\.\S+/.test(formData.email.trim())) {
      nextErrors.email = "Email invalide";
    }
    if (!formData.password) {
      nextErrors.password = "Veuillez renseigner votre mot de passe";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setLoading(false);
      return;
    }

    try {
      await login({
        email: formData.email.trim(),
        password: formData.password,
      });
      toast.success("Connexion réussie !");
      if (typeof onLoginSuccess === "function") {
        onLoginSuccess();
      } else {
        // Retour là où la connexion a été demandée, pas à l'accueil.
        // `replace` : la page de login n'a pas à rester dans l'historique.
        const destination = resolveAuthRedirect(location);
        setTimeout(() => navigate(destination, { replace: true }), 800);
      }
    } catch (error) {
      console.error("Erreur login:", error);
      ErrorHandler.handle(error, {
        showToast: true,
        defaultMessage: "Erreur lors de la connexion",
        onValidationError: (validationErrors) => {
          setErrors(ErrorHandler.extractValidationMessages(validationErrors));
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Auth
      title="Bon retour"
      subtitle="Connecte-toi avec ton email et ton mot de passe."
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-3.5" noValidate>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 ml-0.5">
            Email
          </label>
          <input
            type="email"
            name="email"
            autoComplete="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="ex. toi@email.com"
            className={fieldClass(!!errors.email)}
          />
          {errors.email && (
            <p className="text-red-500 text-xs mt-1.5">{errors.email}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 ml-0.5">
            Mot de passe
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Ton mot de passe"
              className={`${fieldClass(!!errors.password)} pr-11`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
              aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="text-red-500 text-xs mt-1.5">{errors.password}</p>
          )}
          <div className="text-right mt-1.5">
            <Link to="/restore" className="text-xs font-medium text-orange-600 hover:text-orange-700">
              Mot de passe oublié ?
            </Link>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="mt-2 w-full py-3.5 rounded-xl font-bold text-sm text-white bg-orange-500 hover:bg-orange-600 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
        >
          {loading && (
            <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          )}
          {loading ? "Connexion…" : "Se connecter"}
        </button>
      </form>
    </Auth>
  );
};

export default Login;
